import { useCallback, useEffect } from 'react';
import { FACTOR_CONTEXT_CHANGED_EVENT } from '../../../utils/factorContextChange';
import {
    APPBAR_AUTO_SYNC_UPDATE_EVENT,
    APPBAR_CONTEXT_LINEAGE_EVENT,
    APPBAR_SYNC_STATE_EVENT,
    APPBAR_UPLOAD_CONTEXT_EVENT,
    APPBAR_UPLOAD_INTERACTION_EVENT,
    FACTOR_DATA_REFRESH_REQUEST_EVENT,
    RISK_CONTEXT_REFRESH_REQUEST_EVENT,
} from '../dashboardUtils';

export default function useDashboardAppBarBridge({
    enabled = true,
    handleFactorDataRefreshRequest,
    handleRiskContextRefreshRequest,
    handleFactorContextChanged,
    handlePortfolioGuideInteraction,
    onAutoRefreshEnabledChange,
    setPosSizingAutoSyncEnabled,
    setFactorDataAutoSyncEnabled,
    posSizingAutoSyncToggleBusy,
    uploadContext,
    syncState,
    contextLineage,
}) {
    const handleAppBarAutoSyncUpdate = useCallback((event) => {
        const detail = event?.detail || {};
        if (typeof detail.autoRefreshEnabled === 'boolean') {
            onAutoRefreshEnabledChange(detail.autoRefreshEnabled);
        }
        if (
            typeof detail.posSizingAutoSyncEnabled === 'boolean'
            && !posSizingAutoSyncToggleBusy
        ) {
            setPosSizingAutoSyncEnabled(detail.posSizingAutoSyncEnabled);
        }
        if (typeof detail.factorDataAutoSyncEnabled === 'boolean') {
            setFactorDataAutoSyncEnabled(detail.factorDataAutoSyncEnabled);
        }
    }, [onAutoRefreshEnabledChange, posSizingAutoSyncToggleBusy, setPosSizingAutoSyncEnabled, setFactorDataAutoSyncEnabled]);

    useEffect(() => {
        if (!enabled) return undefined;
        if (typeof window === 'undefined') return undefined;

        const handleFactorRefreshRequest = () => {
            void handleFactorDataRefreshRequest();
        };
        const handleRiskContextRefresh = (event) => {
            const detail = event?.detail || {};
            // The Dashboard approves a saved factor vendor's re-check itself;
            // claiming it stops AppBar from approving it a second time.
            if (detail.factorVendorTransition && handleRiskContextRefreshRequest) {
                event.preventDefault();
            }
            void handleRiskContextRefreshRequest?.(detail);
        };
        const handleAutoSyncUpdate = (event) => {
            handleAppBarAutoSyncUpdate(event);
        };
        const handleUploadInteraction = () => {
            handlePortfolioGuideInteraction();
        };
        const handleContextChanged = () => {
            void handleFactorContextChanged?.();
        };

        window.addEventListener(FACTOR_DATA_REFRESH_REQUEST_EVENT, handleFactorRefreshRequest);
        window.addEventListener(RISK_CONTEXT_REFRESH_REQUEST_EVENT, handleRiskContextRefresh);
        window.addEventListener(APPBAR_AUTO_SYNC_UPDATE_EVENT, handleAutoSyncUpdate);
        window.addEventListener(APPBAR_UPLOAD_INTERACTION_EVENT, handleUploadInteraction);
        window.addEventListener(FACTOR_CONTEXT_CHANGED_EVENT, handleContextChanged);
        return () => {
            window.removeEventListener(FACTOR_DATA_REFRESH_REQUEST_EVENT, handleFactorRefreshRequest);
            window.removeEventListener(RISK_CONTEXT_REFRESH_REQUEST_EVENT, handleRiskContextRefresh);
            window.removeEventListener(APPBAR_AUTO_SYNC_UPDATE_EVENT, handleAutoSyncUpdate);
            window.removeEventListener(APPBAR_UPLOAD_INTERACTION_EVENT, handleUploadInteraction);
            window.removeEventListener(FACTOR_CONTEXT_CHANGED_EVENT, handleContextChanged);
        };
    }, [
        enabled,
        handleAppBarAutoSyncUpdate,
        handleFactorContextChanged,
        handleFactorDataRefreshRequest,
        handleRiskContextRefreshRequest,
        handlePortfolioGuideInteraction,
    ]);

    useEffect(() => {
        if (!enabled) return;
        if (typeof window === 'undefined') return;
        window.dispatchEvent(new CustomEvent(APPBAR_UPLOAD_CONTEXT_EVENT, {
            detail: uploadContext,
        }));
    }, [enabled, uploadContext]);

    useEffect(() => {
        if (!enabled) return;
        if (typeof window === 'undefined') return;
        window.dispatchEvent(new CustomEvent(APPBAR_SYNC_STATE_EVENT, {
            detail: syncState,
        }));
    }, [enabled, syncState]);

    useEffect(() => {
        if (!enabled) return;
        if (typeof window === 'undefined') return;
        window.dispatchEvent(new CustomEvent(APPBAR_CONTEXT_LINEAGE_EVENT, {
            detail: contextLineage,
        }));
    }, [contextLineage, enabled]);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        if (!enabled) {
            window.dispatchEvent(new CustomEvent(APPBAR_UPLOAD_CONTEXT_EVENT, {
                detail: { factorContextUpdateProgress: null },
            }));
            window.dispatchEvent(new CustomEvent(APPBAR_CONTEXT_LINEAGE_EVENT, {
                detail: null,
            }));
            return undefined;
        }
        return () => {
            window.dispatchEvent(new CustomEvent(APPBAR_UPLOAD_CONTEXT_EVENT, {
                detail: { factorContextUpdateProgress: null },
            }));
            window.dispatchEvent(new CustomEvent(APPBAR_CONTEXT_LINEAGE_EVENT, {
                detail: null,
            }));
        };
    }, [enabled]);
}
